'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Card} from 'primereact/card';
import {DataTableStateEvent} from 'primereact/datatable';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {PaginatorChangeEvent} from 'primereact/paginator';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import messageUtils from '~/shared/src/utilities/messageUtils';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';
import {
    ItemAttributes,
    ItemAttributesForCreation,
    ItemAttributesSettable,
    ItemCollection
} from '~/shared/src/ci/Item';
import {
    ReferenceAttributes,
    ReferenceAttributesForCreation,
    ReferenceCollection
} from '~/shared/src/ci/Reference';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    DataDescriptor,
    DataStructure,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    LoadColumnDataDescriptorsParameters,
    getColumnsForSqlQuery,
    getColumnDefinitions,
    getFixFilters,
    getDefaultFilters,
    getDefaultSorters,
    LoadDataCommonParameters,
    LoadDataResult
} from '~/shared/src/utilities/modules/overview';
import {
    ColumnConfig,
    ColumnDefinition as OverviewColumnDefinition,
    ColumnFilter,
    ColumnSorter,
    FilterMatchMode,
    FilterValueType,
    RowStyling
} from '~/shared/src/validationSchemes/modules/overview';
import {Column} from '~/shared/src/utilities/column';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {fieldTypeToItemTypeMapping} from '~/shared/src/utilities/modules/form';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';

// frontend imports
import DataTable, {
    DataTable as DataTableClass,
    AllProps as DataTableAllProps,
    ComponentProps as DataTableComponentProps,
    defaultProps as dataTableDefaultProps
} from '~/frontend/src/components/base/DataTable/index';

import {overviewUtilities} from '~/frontend/src/components/base/OverviewDesigner/utilities';
import {
    getColumnConfigFromColumnDefinition,
    getColumnHeader
} from '~/frontend/src/components/base/DataTable/utilities';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import loadData from '~/frontend/src/actions/data/loadData';
import loadDataForDataStructure from '~/frontend/src/actions/panel/overviewDesigner/loadDataForDataStructure';
import loadDataForQuery from '~/frontend/src/actions/panel/overviewDesigner/loadDataForQuery';
import {LoadDataParameters} from '~/shared/src/utilities/modules/chart';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';


const debugColorScheme = {
    error            : 'F44336',
    loadFunction     : '009688',
    cacheFunction    : '607D8B',
    mouseFunction    : '0095ce',
    dragNdropFunction: 'E91E63',
    helperFunction   : 'FF5722',
    render           : '1565C0'
};

type ColumnDefinition = OverviewColumnDefinition & {
    layoutElementId : number;
};

export type Mode = 'design' | 'input';

export type DragData = {
    items : ItemCollection;
    references? : ReferenceCollection;
};

export type DragOptions = {
    label? : string;
    icon? : string;
};

type InsertPosition = 'before' | 'after' | 'insideBefore' | 'insideAfter';
type DragInfo = {
    dragLayoutElementId : number;
    dragRawColumn : Column;
    rawColumns : Column[];
    rawColumnsOrig : Column[];
    dragElement : HTMLDivElement;
    insertMarkerElement : HTMLDivElement;
    lastDragOverLayoutElementId? : number;
    position? : InsertPosition;
    doNothing : boolean;
};

type NewDragInfo = {
    data : DragData;
    rawColumn? : Column;
};

export type DefaultProps = BaseRequestParameters & {
    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    mode? : Mode;
    readOnly? : boolean;
    showData? : boolean;
    showHiddenColumns? : boolean;
    scrollTop? : number;
    scrollLeft? : number;
    standalone? : boolean;
    rootItemFormState? : 'CLEAR' | 'DIRTY' | 'ERROR';
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    additionalElusiveDataContexts: [],
    mode                         : 'design',
    readOnly                     : false,
    showData                     : false,
    showHiddenColumns            : false,
    scrollTop                    : 0,
    scrollLeft                   : 0,
    standalone                   : true,
    rootItemFormState            : 'CLEAR'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    /*** config data ************************************************************************/
    // load from backend

    /**
     * The key of the CFG:Overview item to load the config from.
     */
    viewKey? : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    // OR provided from outside

    /**
     * The uniqueId of the component which has loaded or builds the config (to track config changes)
     */
    configUid? : string;

    /**
     * The dataContext for the form configuration to access global.data with.
     */
    configDataContext? : string;

    /**
     * The id of the root config item of the form.
     */
    configRootItemId : number;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: string
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: string
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];


    /*** design mode only *******************************************************************/

    /**
     * The id of currently selected layout Element.
     */
    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    /**
     * Callback if a layout element displaying data is hovered.
     *
     * @param alias
     */
    onMarkFieldsForAlias? : ( alias : string | string[] | undefined ) => void;


    /*** input mode only ********************************************************************/

};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ElusiveData = PanelElusiveDataState & {
    columnDataDescriptors? : StaticDataDescriptor[];
    columnDefinitions? : ColumnDefinition[];
    columns? : ColumnConfig[];
    dataStructure? : DataStructure;
    filters? : ColumnFilter[];
    fixFilters? : ColumnFilter[];
    isDataLoaded? : boolean;
    lastLoadParameters? : string;
    lastColumnDataDescriptorParameters? : string;
    rawColumns? : Column[];
    rowStyling? : RowStyling[];
    searchColumns? : string[];
    sorters? : ColumnSorter[];
};

export type ReferenceType = {
    dataStructure? : DataStructure;
    columnDefinitions? : ColumnDefinition[];
    columns? : ColumnConfig[];
    filters? : ColumnFilter[];
    fixFilters? : ColumnFilter[];
    hasHiddenColumns : boolean;
    sorters? : ColumnSorter[];
    markLayoutElementsForAlias : ( alias : string | undefined ) => void;
    startDrag : ( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) => void;
    setSorters : ( newSorters : ColumnSorter[] ) => void;
};


const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;
const setParamsForDataTable = origSetParams as ( panelKey : string, panelProps : Partial<DataTableAllProps> ) => SetParamsAction;


const OverviewDesigner = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const startTime                                           = performance.now(),
          dispatch : AppDispatch                              = useDispatch(),
          {
              additionalElusiveDataContexts,
              additionalParameters,
              configRootItemId,
              configUid,
              dataContext,
              mode,
              onMarkFieldsForAlias,
              onSelectLayoutElement,
              readOnly,
              rootItemFormState,
              selectedLayoutElementId,
              showData,
              showHiddenColumns,
              standalone,
              uid
          }                                                   = props,
          dataTableUidExtension                               = 'dataTable',
          configDataContext                                   = props.configDataContext ?? `${props.dataContext}_metaData`,
          user                                                = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                             = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                                  = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          debugState                                          = useSelector( ( state : StoreState ) => state.debugConfig?.overviewDesignerDebug ),
          elusiveData                                         = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          elusiveDataConfigData                               = useSelector( ( state : StoreState ) => configUid ? state.elusiveData?.[ configUid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          dataTableComponentProps                             = useSelector( ( state : StoreState ) => getSubPanelProps( `${uid}_${dataTableUidExtension}`, state ) as DataTableComponentProps | undefined ),
          dragInfo                                            = useRef<DragInfo | null>( null ),
          newDragInfo                                         = useRef<NewDragInfo | null>( null ),
          additionalRenderTime                                = useRef<number>( 0 ),
          lastParams                                          = useRef<any>( null ),
          isLoading                                           = useRef<boolean>( false ),
          isColumnClicked                                     = useRef<boolean>( false ),
          resizingLayoutElementId                             = useRef<number>( 0 ),
          droppedLayoutElementId                              = useRef<number | undefined>( undefined ),
          timeoutId                                           = useRef<NodeJS.Timeout | undefined>(),
          droppedStyleTimeout                                 = useRef<NodeJS.Timeout | undefined>(),
          dataTable                                           = useRef<DataTableClass>( null ),
          [ dataTableHeight, setDataTableHeight ]             = useState<number>( 30 ),
          [ dataTableHeaderHeight, setDataTableHeaderHeight ] = useState<number>( 30 ),
          [ hoverLayoutElementId, setHoverLayoutElementId ]   = useState<number | undefined>( undefined ),
          [ aliasToMark, setAliasToMark ]                     = useState<string[] | undefined>( undefined ),
          getDataSchema                                       = ( dataStructureToUse? : DataStructure ) => {
              return ( dataStructureToUse?.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;
          };

    let dataStructure     = useMemo(
            () => {
                return elusiveData?.dataStructure;
            },
            [
                elusiveData?.dataStructure
            ]
        ),
        columnDefinitions = useMemo(
            () => {
                return elusiveData?.columnDefinitions;
            },
            [
                elusiveData?.columnDefinitions
            ]
        ),
        fixFilters        = useMemo(
            () => {
                return elusiveData?.fixFilters;
            },
            [
                elusiveData?.fixFilters
            ]
        ),
        rowStyling        = useMemo(
            () => {
                return elusiveData?.rowStyling;
            },
            [
                elusiveData?.rowStyling
            ]
        ),
        searchColumns     = useMemo(
            () => {
                return elusiveData?.searchColumns;
            },
            [
                elusiveData?.searchColumns
            ]
        ),
        rawColumns        = useMemo(
            () => {
                return elusiveData?.rawColumns;
            },
            [
                elusiveData?.rawColumns
            ]
        ),
        columns           = useMemo(
            () => {
                return elusiveData?.columns;
            },
            [
                elusiveData?.columns
            ]
        ),
        filters           = useMemo(
            () => {
                return elusiveData?.filters;
            },
            [
                elusiveData?.filters
            ]
        ),
        sorters           = useMemo(
            () => {
                return elusiveData?.sorters;
            },
            [
                elusiveData?.sorters
            ]
        ),
        dataTableFilters  = useMemo(
            () => {
                if ( mode === 'design' ) {
                    return filters;
                }
                else {
                    return dataTableComponentProps?.filters
                }
            },
            [
                mode,
                filters,
                dataTableComponentProps?.filters
            ]
        ),
        dataTableSorters  = useMemo(
            () => {
                if ( mode === 'design' ) {
                    return sorters;
                }
                else {
                    return dataTableComponentProps?.sorters
                }
            },
            [
                mode,
                sorters,
                dataTableComponentProps?.sorters
            ]
        ),
        dataSchema        = getDataSchema( dataStructure );

    const isDataLoaded                            = useMemo(
              () => {
                  return elusiveData?.isDataLoaded ?? false;
              },
              [
                  elusiveData?.isDataLoaded
              ]
          ),
          globalConfigDataVersions                = useMemo(
              () => {
                  return JSON.stringify( elusiveDataConfigData ?? {} );
              },
              [
                  elusiveDataConfigData
              ]
          ),
          debug                                   = useMemo(
              () => {
                  return debugState?.showAll ?? debugState?.dedicated?.[ uid ];
              },
              [
                  debugState
              ]
          ),
          overviewItem                            = useMemo(
              () => {
                  return global.data[ configDataContext ]?.items[ configRootItemId ];
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  configRootItemId
              ]
          ),
          overview                                = useMemo(
              () => {
                  return overviewItem ? dataExtraction.extractOverviewData( overviewItem, configDataContext ) : undefined;
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  overviewItem
              ]
          ),
          query                                   = useMemo(
              () => {
                  return overviewUtilities.getQuery( configDataContext, configRootItemId );
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  configRootItemId
              ]
          ),
          hasHiddenColumns                        = useMemo(
              () => {
                  return columnDefinitions
                      ? columnDefinitions.findIndex( colDef => colDef.hidden ) !== -1
                      : false;
              },
              [
                  columnDefinitions
              ]
          ),
          columnGrouping                          = useMemo(
              () => {
                  let retVal : Pick<DataTableComponentProps, 'rowGroupMode' | 'groupRowsBy' | 'rowGroupHeaderTemplate' | 'expandableRowGroups'> | undefined = undefined;

                  if ( overview?.groupingColumn ) {
                      const groupingField = rawColumns?.find( rC => rC.data.key === overview?.groupingColumn )?.dataDescriptor?.alias;

                      if ( groupingField ) {
                          retVal = {
                              rowGroupMode          : 'subheader',
                              groupRowsBy           : `rowData.${groupingField}`,
                              expandableRowGroups   : true,
                              rowGroupHeaderTemplate: ( rowData : any ) => {
                                  if ( !overview.messages?.groupingHeader ) {
                                      return rowData.rowData[ groupingField ]
                                  }
                                  else {
                                      return messageUtils.getMessageTemplateStringResolverForData( { ...rowData.rowData } )( __( overview.messages.groupingHeader as string ) );
                                  }
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  overview?.groupingColumn,
                  overview?.messages?.groupingHeader,
                  rawColumns
              ]
          ),
          getLoadDataParameters                   = ( dataTablePropsToUse : DataTableAllProps, dataTableFiltersToUse : ColumnFilter[], dataTableSortersToUse : ColumnSorter[] ) : LoadDataCommonParameters => {
              const propsForParams                    = {
                        ...dataTableDefaultProps,
                        ...dataTablePropsToUse
                    },
                    {
                        searchString,
                        page,
                        rows
                    }                                 = propsForParams,
                    retVal : LoadDataCommonParameters = {
                        searchString,
                        filters   : [],
                        sorters   : dataTableSortersToUse,
                        start     : ( page - 1 ) * rows,
                        limit     : rows,
                        parameters: additionalParameters
                    },
                    nonEmptyFilters                   = [];

              // filter out empty filters
              if ( fixFilters ) {
                  for ( let i = fixFilters.length - 1; i >= 0; i-- ) {
                      if ( !!fixFilters[ i ].value || fixFilters[ i ].operator === 'empty' || fixFilters[ i ].operator === 'notEmpty' ) {
                          nonEmptyFilters.push( fixFilters[ i ] );
                      }
                  }
              }
              if ( dataTableFiltersToUse ) {
                  for ( let i = dataTableFiltersToUse.length - 1; i >= 0; i-- ) {
                      if (
                          !nonEmptyFilters.find( filter => filter.field === dataTableFiltersToUse[ i ].field )
                          && ( !!dataTableFiltersToUse[ i ].value || dataTableFiltersToUse[ i ].operator === 'empty' || dataTableFiltersToUse[ i ].operator === 'notEmpty' )
                      ) {
                          nonEmptyFilters.push( {
                              ...dataTableFiltersToUse[ i ]
                          } );
                      }
                  }
              }

              retVal.filters = nonEmptyFilters;

              return retVal;
          },
          load                                    = async ( dataTablePropsToUse : DataTableAllProps, dataTableFiltersToUse : ColumnFilter[], dataTableSortersToUse : ColumnSorter[] ) => {
              const innerStartTime = debug ? performance.now() : 0;

              try {
                  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
                  if (
                      rawColumns
                      && columns
                      && (
                          ( query?.type === 'DataStructure' && dataStructure )
                          || ( query?.type !== 'DataStructure' && query?.query )
                      )
                  ) {
                      const dataTableParams                  = getLoadDataParameters( dataTablePropsToUse, dataTableFiltersToUse, dataTableSortersToUse ),
                            columnsToLoad : DataDescriptor[] = [],
                            searchColumnsToLoad : string[]   = [];

                      let params : string = '';

                      // limit columnsToLoad and searchColumns to visible columns or column has filterIfHidden or loadIfHidden
                      for ( const column of rawColumns ) {
                          if ( column.dataDescriptor ) {
                              const columnFieldName = column.dataDescriptor.alias;

                              if ( columns.findIndex( c => c.field === columnFieldName ) !== -1 || !!column.data.filterIfHidden || !!column.data.loadIfHidden ) {
                                  columnsToLoad.push( column.dataDescriptor );

                                  if ( dataTableParams?.searchString && column.data.isSearchColumn ) {
                                      searchColumnsToLoad.push( column.dataDescriptor.alias );
                                  }
                              }
                          }
                      }

                      if ( query.type !== 'DataStructure' ) {
                          params = JSON.stringify( {
                              schema: dataSchema,
                              type  : query.type,
                              query : query.query,
                              columnsToLoad,
                              searchColumnsToLoad,
                              dataTableParams
                          } );
                      }
                      else if ( dataStructure ) {
                          params = JSON.stringify( {
                              schema: dataSchema,
                              dataStructure,
                              columnsToLoad,
                              searchColumnsToLoad,
                              dataTableParams
                          } );
                      }

                      if ( params !== elusiveData?.lastLoadParameters ) {
                          /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
                          if ( debug ) {
                              console.groupCollapsed( `%c${'%cOverview - %cload%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.loadFunction};`, 'font-weight: normal;' );
                          }

                          isLoading.current = true;

                          dispatch( startRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          let result : LoadDataResult | false = false;

                          if ( query.type !== 'DataStructure' ) {
                              result = await dispatch( loadDataForQuery(
                                  uid,
                                  dataContext,
                                  dataSchema,
                                  standalone ? 'override' : 'keepExisting',
                                  query.type,
                                  query.query,
                                  columnsToLoad,
                                  searchColumnsToLoad,
                                  dataTableParams,
                                  `${uid}_${dataTableUidExtension}`
                              ) );
                          }
                          else if ( dataStructure ) {
                              result = await dispatch( loadDataForDataStructure(
                                  uid,
                                  dataContext,
                                  dataSchema,
                                  standalone ? 'override' : 'keepExisting',
                                  dataStructure,
                                  columnsToLoad,
                                  searchColumnsToLoad,
                                  dataTableParams,
                                  `${uid}_${dataTableUidExtension}`
                              ) );
                          }

                          if ( result && overview?.groupingColumn && result.metaData.rowData.length ) {
                              const groupingField = rawColumns?.find( rC => rC.data.key === overview?.groupingColumn )?.dataDescriptor?.alias;

                              if ( groupingField ) {
                                  const newExpandedRows : DataTableComponentProps[ 'expandedRows' ] = [];

                                  for ( const rowData of result.metaData.rowData ) {
                                      if ( !newExpandedRows.find( eR => Object.values( eR )?.[ 0 ] === rowData[ groupingField ] ) ) {
                                          newExpandedRows.push( { [ `rowData.${groupingField}` ]: rowData[ groupingField ] } );
                                      }
                                  }

                                  dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, {
                                      expandedRows: newExpandedRows
                                  } ) );
                              }
                          }
                          dispatch( setElusiveData( uid, {
                              lastLoadParameters: params,
                              isDataLoaded      : true
                          } ) );

                          dispatch( endRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          isLoading.current = false;

                          if ( debug ) {
                              console.log( `Overview - %cload end - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 106 ), `color: #${debugColorScheme.loadFunction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                              console.groupEnd();
                          }
                      }
                  }
              }
              catch ( e ) {
                  console.error( e );
              }
          },
          showDropZones                           = () => {
              const overviewElement    = document.getElementById( `${uid}_overview` ),
                    overviewElementBox = overviewElement?.getBoundingClientRect();

              if ( overviewElement && overviewElementBox ) {
                  // mask delete drop zone
                  let maskElement = document.createElement( 'div' );

                  maskElement.id = `${uid}_overviewdesigner_delete_zone`;
                  maskElement.className = 'yn-overviewdesigner-delete-zone';
                  maskElement.style.top = `${overviewElementBox.y}px`;
                  maskElement.style.width = `${overviewElementBox.x}px`;
                  maskElement.style.height = `${overviewElementBox.height}px`;
                  document.body.appendChild( maskElement );

                  // mask cancel drop zone
                  maskElement = document.createElement( 'div' );

                  maskElement.id = `${uid}_overviewdesigner_cancel_zone`;
                  maskElement.className = 'yn-overviewdesigner-cancel-zone';
                  maskElement.style.top = `${overviewElementBox.y}px`;
                  maskElement.style.width = `${document.body.clientWidth - overviewElementBox.x - overviewElementBox.width}px`;
                  maskElement.style.height = `${overviewElementBox.height}px`;
                  document.body.appendChild( maskElement );
              }
          },
          hideDropZones                           = () => {
              const deleteZone = document.getElementById( `${uid}_overviewdesigner_delete_zone` ),
                    cancelZone = document.getElementById( `${uid}_overviewdesigner_cancel_zone` );

              // unmask delete zone
              if ( deleteZone ) {
                  document.body.removeChild( deleteZone );
              }

              // unmask cancel zone
              if ( cancelZone ) {
                  document.body.removeChild( cancelZone );
              }
          },
          onDragToDelete                          = () => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragToDelete%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current ) {
                  showDropZones();

                  // mark drag element to be removed
                  dragInfo.current?.dragElement.classList.add( 'yn-overviewdesigner-drag-delete' );

                  if ( newDragInfo.current ) {
                      dragInfo.current.rawColumns = dragInfo.current.rawColumnsOrig;
                      dragInfo.current.doNothing = true;

                      // cleanup
                      dispatch( setElusiveData( uid, {
                          rawColumns: dragInfo.current.rawColumnsOrig
                      } ) );
                  }
                  else if ( dragInfo.current.rawColumns ) {
                      const newRawColumns = JSON.parse( JSON.stringify( dragInfo.current.rawColumns ) ) as Column[],
                            dragRawColumn = dragInfo.current.dragRawColumn;

                      let index = newRawColumns.findIndex( col => col.data.id === dragRawColumn.data.id );

                      // remove from rawColumns
                      if ( index !== -1 ) {
                          newRawColumns.splice( index, 1 );
                      }

                      // reorder columns in rawColumns
                      for ( index = 0; index < newRawColumns.length; index++ ) {
                          newRawColumns[ index ].data.order = 10 * ( index + 1 );
                      }

                      // update dragInfo
                      dragInfo.current.rawColumns = newRawColumns;
                      dragInfo.current.doNothing = false;

                      // update elusiveData
                      dispatch( setElusiveData( uid, {
                          rawColumns: newRawColumns
                      } ) );
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onDragOver                              = ( layoutElementId : number, position : InsertPosition ) => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragOver%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current && ( dragInfo.current?.rawColumns || layoutElementId === overview?.data.id ) ) {
                  const newRawColumns = (
                            dragInfo.current?.rawColumns
                                ? JSON.parse( JSON.stringify( dragInfo.current.rawColumns ) )
                                : []
                        ) as Column[],
                        dragRawColumn = dragInfo.current.dragRawColumn;

                  let index = newRawColumns.findIndex( col => col.data.id === dragRawColumn.data.id );

                  // remove from rawColumns
                  if ( index !== -1 ) {
                      newRawColumns.splice( index, 1 );
                  }

                  // insert into rawColumns
                  index = newRawColumns.findIndex( col => col.data.id === layoutElementId );

                  if ( index !== -1 || !newRawColumns.length ) {
                      if ( !newRawColumns.length ) {
                          newRawColumns.push( dragRawColumn );
                      }
                      else if ( position === 'before' ) {
                          newRawColumns.splice( index, 0, dragRawColumn );
                      }
                      else {
                          newRawColumns.splice( index + 1, 0, dragRawColumn );
                      }

                      // reorder columns in rawColumns
                      for ( index = 0; index < newRawColumns.length; index++ ) {
                          newRawColumns[ index ].data.order = 10 * ( index + 1 );
                      }

                      // mark drag element ok
                      dragInfo.current?.dragElement.classList.add( 'yn-overviewdesigner-drag-ok' );

                      // update dragInfo
                      dragInfo.current.rawColumns = newRawColumns;
                      dragInfo.current.doNothing = false;

                      // update elusiveData
                      dispatch( setElusiveData( uid, {
                          rawColumns: newRawColumns
                      } ) );
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onDragOut                               = () => {
              if ( debug ) {
                  console.log( `%c${`%conDragOut%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( dragInfo.current ) {
                  showDropZones();

                  // mark drag element to not changed
                  dragInfo.current.dragElement.classList.add( 'yn-overviewdesigner-drag-nothing' );

                  // update dragInfo
                  dragInfo.current.rawColumns = dragInfo.current.rawColumnsOrig;
                  dragInfo.current.doNothing = true;

                  // cleanup
                  dispatch( setElusiveData( uid, {
                      rawColumns: dragInfo.current.rawColumnsOrig
                  } ) );
              }
          },
          onDrop                                  = () => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDrop%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current && !dragInfo.current.doNothing ) {
                  const propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 );

                  // add given drag data
                  if ( newDragInfo.current ) {
                      if ( debug ) {
                          console.log( '-> adding new data' );
                      }

                      const oldToNewItemIdMapping : {
                          [ oldId : string ] : string
                      } = {};

                      for ( const item of Object.values( newDragInfo.current.data.items ) ) {
                          const itemAttributes = item.toJSON( 'all' ) as ItemAttributes;

                          // delete id
                          if ( item.id < 0 ) {
                              delete itemAttributes.id;
                          }
                          // delete new ids for create properties
                          for ( const property of itemAttributes.properties ?? [] ) {
                              if ( ( property.id ?? 0 ) < 0 ) {
                                  delete property.id;
                              }
                          }
                          // delete new ids for create enumProperties
                          for ( const enumProperty of itemAttributes.enumProperties ?? [] ) {
                              for ( const property of enumProperty.properties ?? [] ) {
                                  if ( ( property.id ?? 0 ) < 0 ) {
                                      delete property.id;
                                  }
                              }
                          }

                          if ( debug ) {
                              console.log( '    -> adding new item', itemAttributes );
                          }

                          const newItem = dispatch( addItem(
                              configDataContext,
                              itemAttributes as ItemAttributesForCreation,
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          if ( newItem ) {
                              // update dragItem with newItem
                              if ( item.id === dragInfo.current.dragRawColumn.data.id ) {
                                  const column = dragInfo.current.rawColumns.find( col => col.data.id === dragInfo.current!.dragRawColumn.data.id );

                                  if ( column ) {
                                      column.data.id = newItem.id;
                                  }
                              }

                              if ( item.id < 0 ) {
                                  oldToNewItemIdMapping[ String( item.id ) ] = String( newItem.id );
                              }
                          }
                      }
                      if ( newDragInfo.current.data.references ) {
                          for ( const reference of Object.values( newDragInfo.current.data.references ) ) {
                              const referenceAttributes = reference.toJSON() as ReferenceAttributes;

                              // remap item ids
                              if ( oldToNewItemIdMapping[ String( reference.srcItemId ) ] ) {
                                  referenceAttributes.srcItemId = Number( oldToNewItemIdMapping[ String( reference.srcItemId ) ] );
                              }
                              if ( oldToNewItemIdMapping[ String( reference.destItemId ) ] ) {
                                  referenceAttributes.destItemId = Number( oldToNewItemIdMapping[ String( reference.destItemId ) ] );
                              }

                              // delete new ids for create reference
                              if ( reference.id < 0 ) {
                                  delete referenceAttributes.id;
                              }
                              // delete new ids for create referenceProperties
                              for ( const property of referenceAttributes.properties ?? [] ) {
                                  if ( ( property.id ?? 0 ) < 0 ) {
                                      delete property.id;
                                  }
                              }
                              // delete new ids for create enumReferenceProperties
                              for ( const enumProperty of referenceAttributes.enumProperties ?? [] ) {
                                  for ( const property of enumProperty.properties ?? [] ) {
                                      if ( ( property.id ?? 0 ) < 0 ) {
                                          delete property.id;
                                      }
                                  }
                              }

                              if ( debug ) {
                                  console.log( '    -> adding new reference', referenceAttributes );
                              }

                              dispatch( addReference(
                                  configDataContext,
                                  referenceAttributes as ReferenceAttributesForCreation,
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      // if something has really changed (parent, position within) apply drop effect
                      droppedLayoutElementId.current = dragInfo.current.dragRawColumn.data.id;

                      // select dropped item
                      lastParams.current?.onSelectLayoutElement?.( dragInfo.current.dragRawColumn.data.id );
                  }

                  // remove
                  else if ( !dragInfo.current.lastDragOverLayoutElementId ) {
                      if ( debug ) {
                          console.log( '-> processing delete' );
                      }

                      const oldItem = global.data[ configDataContext ].items[ dragInfo.current.dragLayoutElementId ];

                      if ( oldItem ) {
                          if ( debug ) {
                              console.log( '    -> deleting item', oldItem.toJSON() );
                          }

                          dispatch( deleteItem(
                              configDataContext,
                              oldItem.id,
                              undefined,
                              additionalElusiveDataContexts
                          ) );

                          // delete all other references
                          for ( const referenceId of oldItem.references ) {
                              dispatch( deleteReference(
                                  configDataContext,
                                  referenceId,
                                  undefined,
                                  additionalElusiveDataContexts
                              ) );
                          }

                          lastParams.current?.onSelectLayoutElement?.( configRootItemId );
                      }
                  }
                  else {
                      droppedLayoutElementId.current = dragInfo.current.dragLayoutElementId;
                  }

                  // set orders
                  if ( debug ) {
                      console.log( '    -> reordering layout elements in parent' );
                  }

                  for ( const column of dragInfo.current.rawColumns ) {
                      const columnItem = global.data[ configDataContext ].items[ column.data.id ];

                      if ( columnItem ) {
                          const data : ItemAttributesSettable = {
                              properties: {
                                  [ propertyTypeIdString ]: {
                                      value: column.data.order
                                  }
                              }
                          };

                          dispatch( updateItem(
                              configDataContext,
                              columnItem.id,
                              data,
                              additionalElusiveDataContexts
                          ) );
                      }
                  }

                  dispatch( setElusiveData( uid, {
                      rawColumns: buildRawColumns( dataStructure, elusiveData?.columnDataDescriptors )
                  } ) );
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onMouseMove                             = ( e : MouseEvent ) : void => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseMove%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              let layoutElementId : number | undefined,
                  position : InsertPosition | undefined;

              if ( dragInfo.current ) {
                  // set position of drag ghost
                  dragInfo.current.dragElement.style.left = ( e.pageX - 50 ) + 'px';
                  dragInfo.current.dragElement.style.top = ( e.pageY - 50 ) + 'px';

                  // get drag over layoutElement
                  if ( dragInfo.current.rawColumns ) {
                      const overviewElement    = document.getElementById( `${uid}_overview` ),
                            overviewElementBox = overviewElement?.getBoundingClientRect(),
                            cleanMarker        = () => {
                                hideDropZones();

                                // unmark drag element
                                dragInfo.current?.dragElement.classList.remove( 'yn-overviewdesigner-drag-ok', 'yn-overviewdesigner-drag-nothing', 'yn-overviewdesigner-drag-delete' );
                            };

                      // get column under pointer
                      for ( const rawColumn of dragInfo.current.rawColumns ) {
                          const element = document.getElementById( `leid_${rawColumn.data.id}` );

                          if ( element ) {
                              const elementBox = element.getBoundingClientRect();

                              if (
                                  e.pageX >= elementBox.x && e.pageX <= ( elementBox.x + elementBox.width )
                                  && e.pageY >= elementBox.y && e.pageY <= ( elementBox.y + dataTableHeight )
                              ) {
                                  layoutElementId = rawColumn.data.id;

                                  position = 'after';
                                  if ( e.pageX <= ( elementBox.x + elementBox.width / 2 ) ) {
                                      position = 'before';
                                  }

                                  break;
                              }
                          }
                      }

                      // no layoutElementId and pointer within overview -> set to overview layoutElementId
                      if ( !layoutElementId ) {
                          if (
                              overviewElementBox
                              && e.pageX > overviewElementBox.x && e.pageX < ( overviewElementBox.x + overviewElementBox.width )
                              && e.pageY > overviewElementBox.y && e.pageY < ( overviewElementBox.y + overviewElementBox.height )
                          ) {
                              layoutElementId = configRootItemId;

                              position = 'insideAfter';
                              if ( e.pageX <= ( overviewElementBox.x + overviewElementBox.width / 2 ) ) {
                                  position = 'insideBefore';
                              }
                          }
                      }

                      if ( debug ) {
                          console.log( '    layoutElementId', layoutElementId );
                          console.log( '    position', position );
                      }

                      // drag over layoutElement found
                      // and position
                      // and dragged layoutElement not the drag over layoutElement
                      // and drop target layoutElement or position changed
                      if (
                          layoutElementId
                          && position
                          && dragInfo.current.dragLayoutElementId !== layoutElementId
                          && ( layoutElementId !== dragInfo.current.lastDragOverLayoutElementId || position !== dragInfo.current.position )
                      ) {
                          // mark drag over element (hover)
                          if ( lastParams.current?.hoverLayoutElementId !== layoutElementId ) {
                              if ( debug ) {
                                  console.log( '    -> set hoverLayoutElementId' );
                              }

                              setHoverLayoutElementId( layoutElementId );
                          }

                          dragInfo.current.lastDragOverLayoutElementId = layoutElementId;
                          dragInfo.current.position = position;

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout(
                              () => {
                                  clearTimeout( timeoutId.current );

                                  cleanMarker();

                                  // handle drag over
                                  onDragOver( layoutElementId!, position! );
                              },
                              100
                          );
                      }
                      else if ( !layoutElementId ) {
                          delete dragInfo.current.lastDragOverLayoutElementId;
                          delete dragInfo.current.position;

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }

                          // dragged to drag out (delete) zone
                          if (
                              overviewElementBox
                              && e.pageX < overviewElementBox.x
                              && e.pageY > overviewElementBox.y
                          ) {
                              timeoutId.current = setTimeout(
                                  () => {
                                      clearTimeout( timeoutId.current );

                                      cleanMarker();

                                      // handle delete
                                      onDragToDelete();
                                  },
                                  100
                              );
                          }
                          else {
                              timeoutId.current = setTimeout(
                                  () => {
                                      clearTimeout( timeoutId.current );

                                      cleanMarker();

                                      // handle drag out
                                      onDragOut();
                                  },
                                  100
                              );
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onMouseUp                               = ( e : MouseEvent | React.MouseEvent<HTMLDivElement> ) : void => {
              if ( debug ) {
                  console.log( `%c${`%conMouseUp%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              // remove listeners for drag control
              document.querySelector( 'html' )?.removeEventListener( 'mouseup', onMouseUp );
              document.querySelector( 'html' )?.removeEventListener( 'mousemove', onMouseMove );

              // destroy drag ghost
              if ( dragInfo.current?.dragElement ) {
                  document.body.removeChild( dragInfo.current.dragElement );
              }

              hideDropZones();

              onDrop();

              // clean drag info
              dragInfo.current = null;
              // clean new drag info
              newDragInfo.current = null;

              setHoverLayoutElementId( undefined );
              lastParams.current.onMarkFieldsForAlias?.( undefined );
          },
          onDragStart                             = ( layoutElementId : number, e : React.DragEvent<HTMLElement>, options? : DragOptions ) : void => {
              const innerStartTime         = debug ? performance.now() : 0,
                    rawColumnsToUse        = lastParams.current?.rawColumns as Column[] | undefined,
                    dataStructureToUse     = lastParams.current?.dataStructure as DataStructure | undefined,
                    columnDefinitionsToUse = lastParams.current?.columnDefinitions as ColumnDefinition[] | undefined;

              if ( !resizingLayoutElementId.current && rawColumnsToUse ) {
                  const rawColumn = newDragInfo.current?.rawColumn ?? rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

                  if ( rawColumn ) {
                      const columnDefinition = columnDefinitionsToUse?.find( colDef => colDef.field === rawColumn.dataDescriptor?.alias );

                      if ( !columnDefinition || !columnDefinition.hidden ) {
                          if ( debug ) {
                              console.groupCollapsed( `%c${`%conDragStart - ${rawColumn?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
                          }

                          // init drag info
                          dragInfo.current = {
                              dragLayoutElementId: layoutElementId,
                              dragRawColumn      : rawColumn,
                              rawColumns         : rawColumnsToUse,
                              rawColumnsOrig     : rawColumnsToUse,
                              dragElement        : document.createElement( 'div' ),
                              insertMarkerElement: document.createElement( 'div' ),
                              doNothing          : true
                          };

                          if ( newDragInfo.current ) {
                              // use only for init drag info
                              delete newDragInfo.current.rawColumn;
                          }

                          // create drag ghost
                          const fieldPreview       = document.createElement( 'div' ),
                                layoutElementLabel = options?.label
                                    ?? ( columnDefinition && dataStructureToUse
                                            ? getColumnHeader( columnDefinition, dataStructureToUse, ( rawColumn.dataDescriptor?.dataObject?.item.objectSchema ?? 'DATA' ) as Schema, false, false )
                                            : 'Column'
                                    ),
                                valueType          = rawColumn.dataDescriptor
                                    ? dataUtils.getDataValueType( rawColumn.dataDescriptor, 'CFG', dataStructure )
                                    : undefined,
                                layoutElementIcon  = options?.icon ?? `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( valueType ? fieldTypeToItemTypeMapping[ valueType as PropertyValueType ] : '' )?.icon}`;

                          dragInfo.current.dragElement.id = 'drag';
                          dragInfo.current.dragElement.className = 'yn-overviewdesigner-drag';
                          dragInfo.current.dragElement.style.left = ( e.pageX - 50 ) + 'px';
                          dragInfo.current.dragElement.style.top = ( e.pageY - 50 ) + 'px';
                          document.body.appendChild( dragInfo.current.dragElement );

                          fieldPreview.id = 'drag_preview';
                          fieldPreview.className = 'yn-overviewdesigner-drag-preview p-component yn-fieldlike';
                          fieldPreview.innerHTML = `<span class='${layoutElementIcon}'></span>${layoutElementLabel}`;
                          dragInfo.current.dragElement.appendChild( fieldPreview );

                          // add listeners for drag control
                          document.querySelector( 'html' )?.addEventListener( 'mousemove', onMouseMove );
                          document.querySelector( 'html' )?.addEventListener( 'mouseup', onMouseUp );

                          if ( debug ) {
                              console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                              console.groupEnd();
                          }
                      }
                  }
              }

              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.preventDefault();
          },
          onMouseEnter                            = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const rawColumnsToUse = lastParams.current?.rawColumns as Column[],
                    rawColumn       = rawColumnsToUse?.find( ( col : Column ) => col.data.id === layoutElementId );

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseEnter - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              e.stopPropagation();

              if ( !resizingLayoutElementId.current && lastParams.current?.hoverLayoutElementId !== layoutElementId ) {
                  if ( debug ) {
                      console.log( '-> set hoverLayoutElementId', layoutElementId );
                  }

                  lastParams.current.hoverLayoutElementId = layoutElementId;
                  setHoverLayoutElementId( layoutElementId );
                  lastParams.current?.onMarkFieldsForAlias?.( rawColumn?.dataDescriptor?.fullAlias.split( ',' ) );
              }

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onMouseLeave                            = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const rawColumnsToUse = lastParams.current?.rawColumns,
                    rawColumn       = rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseLeave - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( !resizingLayoutElementId.current && lastParams.current?.hoverLayoutElementId === layoutElementId ) {
                  if ( debug ) {
                      console.log( '-> unset hoverLayoutElementId' );
                  }

                  lastParams.current.hoverLayoutElementId = undefined;
                  setHoverLayoutElementId( undefined );
                  lastParams.current?.onMarkFieldsForAlias?.( undefined );
              }

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onClick                                 = ( layoutElementId : number | undefined, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const element        = document.getElementById( `leid_${layoutElementId}` ),
                    resizerElement = element?.querySelector( '.p-column-resizer' ),
                    elementBox     = element?.getBoundingClientRect();

              if ( debug ) {
                  const rawColumnsToUse = lastParams.current?.rawColumns,
                        rawColumn       = rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

                  console.log( `%c${`%conClick - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( ( e.target === element && ( !elementBox || e.pageY > ( elementBox.y + dataTableHeaderHeight ) ) ) || e.target === resizerElement ) {
                  const resizerElementBox  = resizerElement?.getBoundingClientRect(),
                        deleteElementStyle = resizerElement
                            ? window.getComputedStyle( resizerElement, ':after' )
                            : undefined,
                        deleteElementBox   = resizerElementBox && deleteElementStyle
                            ? {
                                x     : resizerElementBox.x + parseFloat( deleteElementStyle.left ),
                                width : parseFloat( deleteElementStyle.width ) + parseFloat( deleteElementStyle.paddingLeft ) + parseFloat( deleteElementStyle.paddingRight ) + parseFloat( deleteElementStyle.marginLeft ) + parseFloat( deleteElementStyle.marginRight ),
                                y     : resizerElementBox.y + parseFloat( deleteElementStyle.top ),
                                height: parseFloat( deleteElementStyle.height ) + parseFloat( deleteElementStyle.paddingTop ) + parseFloat( deleteElementStyle.paddingBottom ) + parseFloat( deleteElementStyle.marginTop ) + parseFloat( deleteElementStyle.marginBottom )
                            }
                            : undefined;

                  // check if selected and delete is clicked
                  if (
                      layoutElementId === lastParams.current?.selectedLayoutElementId
                      && deleteElementBox
                      && e.pageX > deleteElementBox.x
                      && e.pageX < ( deleteElementBox.x + deleteElementBox.width )
                      && e.pageY > deleteElementBox.y
                      && e.pageY < ( deleteElementBox.y + deleteElementBox.height )
                  ) {
                      const rawColumnsToUse      = ( [] as Column[] ).concat( lastParams.current?.rawColumns ),
                            propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 ),
                            oldItem              = global.data[ configDataContext ].items[ layoutElementId ?? 0 ];

                      let index = rawColumnsToUse.findIndex( col => col.data.id === layoutElementId );

                      if ( debug ) {
                          console.log( '-> processing delete' );
                      }

                      if ( oldItem ) {
                          if ( debug ) {
                              console.log( '    -> deleting item', oldItem.toJSON() );
                          }

                          dispatch( deleteItem(
                              configDataContext,
                              oldItem.id,
                              undefined,
                              additionalElusiveDataContexts
                          ) );

                          // delete all other references
                          for ( const referenceId of oldItem.references ) {
                              dispatch( deleteReference(
                                  configDataContext,
                                  referenceId,
                                  undefined,
                                  additionalElusiveDataContexts
                              ) );
                          }

                          // remove from rawColumns
                          if ( index !== -1 ) {
                              rawColumnsToUse.splice( index, 1 );
                          }

                          // set orders
                          for ( index = 0; index < rawColumnsToUse.length; index++ ) {
                              const columnItem = global.data[ configDataContext ].items[ rawColumnsToUse[ index ].data.id ];

                              if ( columnItem ) {
                                  const data : ItemAttributesSettable = {
                                      properties: {
                                          [ propertyTypeIdString ]: {
                                              value: 10 * ( index + 1 )
                                          }
                                      }
                                  };

                                  dispatch( updateItem(
                                      configDataContext,
                                      columnItem.id,
                                      data,
                                      additionalElusiveDataContexts
                                  ) );
                              }
                          }

                          lastParams.current?.onSelectLayoutElement?.( configRootItemId );

                          dispatch( setElusiveData( uid, {
                              rawColumns: buildRawColumns( dataStructure, elusiveData?.columnDataDescriptors )
                          } ) );
                      }
                  }
                  else {
                      lastParams.current?.onSelectLayoutElement?.( layoutElementId );
                  }

                  e.preventDefault();
                  e.stopPropagation();
              }
              else if ( layoutElementId === lastParams.current?.selectedLayoutElementId ) {
                  isColumnClicked.current = true;
              }
              else {
                  lastParams.current?.onSelectLayoutElement?.( layoutElementId );
                  e.preventDefault();
                  e.stopPropagation();
              }
          },
          onColumnResize                          = ( e : MouseEvent ) => {
              const rawColumnsToUse = lastParams.current?.rawColumns;

              if ( resizingLayoutElementId.current && rawColumnsToUse ) {
                  const newRawColumns = JSON.parse( JSON.stringify( rawColumnsToUse ) ) as Column[],
                        rawColumn     = newRawColumns.find( col => col.data.id === resizingLayoutElementId.current ),
                        element       = document.getElementById( `leid_${resizingLayoutElementId.current}` ),
                        elementBox    = element?.getBoundingClientRect();

                  if ( rawColumn && elementBox ) {
                      const width = Math.round( e.pageX - elementBox.x );
                      rawColumn.data.width = width;

                      dataTable.current?.dataTable?.resetResizeColumnsWidth();
                      dispatch( ( setElusiveData( uid, { rawColumns: newRawColumns } ) ) );
                  }
              }
          },
          onColumnResizeStart                     = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) => {
              if ( debug ) {
                  const rawColumnsToUse = lastParams.current?.rawColumns,
                        rawColumn       = rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

                  console.log( `%c${`%conColumnResizeStart - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const target             = e.target as HTMLElement,
                    targetBoundingBox  = target.getBoundingClientRect(),
                    deleteElementStyle = window.getComputedStyle( target, ':after' ),
                    deleteElementBox   = targetBoundingBox && deleteElementStyle
                        ? {
                            x     : targetBoundingBox.x + parseFloat( deleteElementStyle.left ),
                            width : parseFloat( deleteElementStyle.width ) + parseFloat( deleteElementStyle.paddingLeft ) + parseFloat( deleteElementStyle.paddingRight ) + parseFloat( deleteElementStyle.marginLeft ) + parseFloat( deleteElementStyle.marginRight ),
                            y     : targetBoundingBox.y + parseFloat( deleteElementStyle.top ),
                            height: parseFloat( deleteElementStyle.height ) + parseFloat( deleteElementStyle.paddingTop ) + parseFloat( deleteElementStyle.paddingBottom ) + parseFloat( deleteElementStyle.marginTop ) + parseFloat( deleteElementStyle.marginBottom )
                        }
                        : undefined;

              // check if selected and delete is clicked
              if (
                  layoutElementId === lastParams.current?.selectedLayoutElementId
                  && deleteElementBox
                  && e.pageX > deleteElementBox.x
                  && e.pageX < ( deleteElementBox.x + deleteElementBox.width )
                  && e.pageY > deleteElementBox.y
                  && e.pageY < ( deleteElementBox.y + deleteElementBox.height )
              ) {
                  const rawColumnsToUse      = ( [] as Column[] ).concat( lastParams.current?.rawColumns ),
                        propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 ),
                        oldItem              = global.data[ configDataContext ].items[ layoutElementId ];

                  let index = rawColumnsToUse.findIndex( col => col.data.id === layoutElementId );

                  if ( debug ) {
                      console.log( '-> processing delete' );
                  }

                  if ( oldItem ) {
                      if ( debug ) {
                          console.log( '    -> deleting item', oldItem.toJSON() );
                      }

                      dispatch( deleteItem(
                          configDataContext,
                          oldItem.id,
                          undefined,
                          additionalElusiveDataContexts
                      ) );

                      // delete all other references
                      for ( const referenceId of oldItem.references ) {
                          dispatch( deleteReference(
                              configDataContext,
                              referenceId,
                              undefined,
                              additionalElusiveDataContexts
                          ) );
                      }

                      // remove from rawColumns
                      if ( index !== -1 ) {
                          rawColumnsToUse.splice( index, 1 );
                      }

                      // set orders
                      for ( index = 0; index < rawColumnsToUse.length; index++ ) {
                          const columnItem = global.data[ configDataContext ].items[ rawColumnsToUse[ index ].data.id ];

                          if ( columnItem ) {
                              const data : ItemAttributesSettable = {
                                  properties: {
                                      [ propertyTypeIdString ]: {
                                          value: 10 * ( index + 1 )
                                      }
                                  }
                              };

                              dispatch( updateItem(
                                  configDataContext,
                                  columnItem.id,
                                  data,
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      lastParams.current?.onSelectLayoutElement?.( configRootItemId );

                      dispatch( setElusiveData( uid, {
                          rawColumns: buildRawColumns( dataStructure, elusiveData?.columnDataDescriptors )
                      } ) );
                  }
              }
              else {
                  // add listeners for resize control
                  document.querySelector( 'html' )?.addEventListener( 'mouseup', onColumnResizeEnd );
                  document.querySelector( 'html' )?.addEventListener( 'mousemove', onColumnResize );

                  resizingLayoutElementId.current = layoutElementId;
              }

              e.stopPropagation();
              e.nativeEvent.stopPropagation();
          },
          onColumnResizeEnd                       = ( e : MouseEvent ) => {
              const rawColumnsToUse = lastParams.current?.rawColumns as Column[] | undefined,
                    rawColumn       = rawColumnsToUse?.find( col => col.data.id === resizingLayoutElementId.current );

              // remove listeners for resize control
              document.querySelector( 'html' )?.removeEventListener( 'mouseup', onColumnResizeEnd );
              document.querySelector( 'html' )?.removeEventListener( 'mousemove', onColumnResize );

              // set width
              if ( rawColumn ) {
                  const propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Width' )?.id ?? 0 );

                  dataTable.current?.dataTable?.resetResizeColumnsWidth();

                  dispatch( updateItem(
                      configDataContext,
                      rawColumn.data.id,
                      {
                          properties: {
                              [ propertyTypeIdString ]: {
                                  value: rawColumn.data.width
                              }
                          }
                      },
                      additionalElusiveDataContexts
                  ) );
              }
          },
          onOverviewClick                         = () => {
              if ( !resizingLayoutElementId.current && !isColumnClicked.current ) {
                  lastParams.current?.onSelectLayoutElement?.( configRootItemId );
              }
              else {
                  resizingLayoutElementId.current = 0;
                  isColumnClicked.current = false;
              }
          },
          onOverviewLeave                         = ( e : React.MouseEvent<HTMLDivElement> ) : void => {
              setHoverLayoutElementId( undefined );
              lastParams.current?.onMarkFieldsForAlias?.( undefined );
          },
          onSort                                  = ( event : DataTableStateEvent ) => {
              const rawColumnsToUse                   = lastParams.current?.rawColumns as Column[],
                    columnsDataToSet : {
                        [ itemId : number ] : ItemAttributesSettable
                    }                                 = {},
                    activeStatusId                    = metaModel[ 'CFG' ].getStatus( 'active' )?.id,
                    deletedStatusId                   = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id,
                    sortOrderPropertyTypeIdString     = String( metaModel[ 'CFG' ].getPropertyType( 'SortOrder' )?.id ?? 0 ),
                    sortDirectionPropertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'SortDirection' )?.id ?? 0 );

              // clear old sort
              for ( const rawColumn of rawColumnsToUse ) {
                  const columnItem = global.data[ configDataContext ].items[ rawColumn.data.id ];

                  if ( columnItem ) {
                      columnsDataToSet[ rawColumn.data.id ] = {
                          properties: {}
                      };

                      if ( columnItem.properties.has( Number( sortOrderPropertyTypeIdString ) ) ) {
                          columnsDataToSet[ rawColumn.data.id ].properties![ sortOrderPropertyTypeIdString ] = {
                              statusId: deletedStatusId
                          };
                      }

                      if ( columnItem.properties.has( Number( sortDirectionPropertyTypeIdString ) ) ) {
                          columnsDataToSet[ rawColumn.data.id ].properties![ sortDirectionPropertyTypeIdString ] = {
                              statusId: deletedStatusId
                          };
                      }
                  }
              }

              // set new sort
              if ( event.multiSortMeta ) {
                  for ( let index = 0; index < event.multiSortMeta.length; index++ ) {
                      const rawColumn = rawColumnsToUse.find( rC => rC.dataDescriptor?.alias === event.multiSortMeta![ index ].field );

                      if ( rawColumn ) {
                          columnsDataToSet[ rawColumn.data.id ] = {
                              properties: {
                                  [ sortOrderPropertyTypeIdString ]    : {
                                      value   : index + 1,
                                      statusId: activeStatusId
                                  },
                                  [ sortDirectionPropertyTypeIdString ]: {
                                      value   : event.multiSortMeta[ index ].order === 1 ? 'ASC' : 'DESC',
                                      statusId: activeStatusId
                                  }
                              }
                          }
                      }
                  }
              }

              // set sort
              for ( const columnItemId of Object.keys( columnsDataToSet ) ) {
                  dispatch( updateItem(
                      configDataContext,
                      columnItemId,
                      columnsDataToSet[ columnItemId ],
                      additionalElusiveDataContexts
                  ) );
              }

              isColumnClicked.current = true;
          },
          onFilterChange                          = ( key : string, field : string, matchMode : FilterMatchMode, value : any, valueType : FilterValueType ) => {
              const rawColumnsToUse = lastParams.current?.rawColumns as Column[],
                    rawColumn       = rawColumnsToUse.find( rC => rC.dataDescriptor?.alias === field );

              if ( rawColumn ) {
                  const activeStatusId                            = metaModel[ 'CFG' ].getStatus( 'active' )?.id,
                        deletedStatusId                           = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id,
                        filterOperatorPropertyTypeIdString        = String( metaModel[ 'CFG' ].getPropertyType( 'FilterOperator' )?.id ?? 0 ),
                        filterValuePropertyTypeIdString           = String( metaModel[ 'CFG' ].getPropertyType( 'FilterValue' )?.id ?? 0 ),
                        columnsDataToSet : ItemAttributesSettable = {
                            properties: {}
                        },
                        columnItem                                = global.data[ configDataContext ].items[ rawColumn.data.id ];

                  if ( columnItem ) {
                      if ( columnItem.properties.has( Number( filterOperatorPropertyTypeIdString ) ) ) {
                          columnsDataToSet.properties![ filterOperatorPropertyTypeIdString ] = {
                              statusId: deletedStatusId
                          };
                      }

                      if ( columnItem.properties.has( Number( filterValuePropertyTypeIdString ) ) ) {
                          columnsDataToSet.properties![ filterValuePropertyTypeIdString ] = {
                              statusId: deletedStatusId
                          };
                      }
                  }

                  if ( value !== '' ) {
                      columnsDataToSet.properties![ filterOperatorPropertyTypeIdString ] = {
                          value   : matchMode,
                          statusId: activeStatusId
                      };

                      if ( matchMode !== 'empty' && matchMode !== 'notEmpty' ) {
                          columnsDataToSet.properties![ filterValuePropertyTypeIdString ] = {
                              value   : value,
                              statusId: activeStatusId
                          };
                      }
                  }

                  dispatch( updateItem(
                      configDataContext,
                      rawColumn.data.id,
                      columnsDataToSet,
                      additionalElusiveDataContexts
                  ) );
              }
          },
          onFilterEnterKey                        = () => {
              // do nothing
          },
          onRowsPerPageChange                     = useCallback(
              ( event : DropdownChangeEvent ) => {
                  if ( overviewItem ) {
                      const propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'RowsPerPage' )?.id ?? 0 );

                      dispatch( updateItem(
                          configDataContext,
                          overviewItem.id,
                          {
                              properties: {
                                  [ propertyTypeIdString ]: {
                                      value: event.value
                                  }
                              }
                          },
                          additionalElusiveDataContexts
                      ) );
                  }
              },
              [
                  additionalElusiveDataContexts,
                  configDataContext,
                  overviewItem
              ]
          ),
          buildDataStructure                      = () : DataStructure | undefined => {
              let retVal : DataStructure | undefined;

              if ( query ) {
                  if ( query.type === 'DataStructure' ) {
                      retVal = query.dataStructure;
                  }
                  else {
                      retVal = getDataStructureForSqlQuery();
                  }
              }

              return retVal;
          },
          buildRawColumns                         = ( dataStructureToUse = dataStructure, columnDataDescriptors = elusiveData?.columnDataDescriptors ) => {
              let retVal : Column[] | undefined = undefined;

              if ( overviewItem && dataStructureToUse ) {
                  if ( query?.type === 'DataStructure' ) {
                      retVal = dataExtraction.extractColumns( overviewItem, configDataContext, dataStructureToUse );
                  }
                  else if ( columnDataDescriptors ) {
                      retVal = getColumnsForSqlQuery( columnDataDescriptors, overviewItem, configDataContext, mode === 'design', mode === 'design' );
                  }
              }

              return retVal;
          },
          buildColumnDefinitionsFiltersAndSorters = ( dataStructureToUse = dataStructure, rawColumnsToUse = rawColumns ) => {
              const retVal : {
                  columnDefinitions? : ColumnDefinition[];
                  fixFilters? : ColumnFilter[];
                  filters? : ColumnFilter[];
                  sorters? : ColumnSorter[];
              } = {};

              if ( dataStructureToUse && rawColumnsToUse ) {
                  const columnDefinitionsToProcess = getColumnDefinitions( rawColumnsToUse, dataStructureToUse, dataSchema );

                  retVal.columnDefinitions = [];
                  for ( const columnDefinition of columnDefinitionsToProcess ) {
                      const column = rawColumnsToUse.find( ( col : Column ) => col.dataDescriptor === columnDefinition.dataDescriptor );

                      retVal.columnDefinitions.push( {
                          layoutElementId: column?.data.id ?? 0,
                          ...columnDefinition
                      } );
                  }
                  retVal.fixFilters = getFixFilters( rawColumnsToUse, dataStructureToUse );
                  retVal.filters = getDefaultFilters( rawColumnsToUse, dataStructureToUse );
                  retVal.sorters = getDefaultSorters( rawColumnsToUse );
              }

              return retVal;
          },
          buildRowStyling                         = ( dataStructureToUse = dataStructure ) => {
              return dataStructureToUse ? dataExtraction.extractStyles( overviewItem, configDataContext, dataStructureToUse )?.style : undefined;
          },
          buildColumns                            = ( columnDefinitionsToUse = columnDefinitions ) => {
              const retVal : ColumnConfig[] = [];

              if ( columnDefinitionsToUse ) {
                  for ( const columnDefinition of columnDefinitionsToUse ) {
                      const realColumn = !columnDefinition.dataDescriptor?.fullAlias.match( /^createdDataDescriptor::/ );

                      if ( ( realColumn && !columnDefinition.hidden ) || ( mode === 'design' && ( !realColumn || !columnDefinition.hidden || showHiddenColumns ) ) ) {
                          retVal.push( getColumnConfigFromColumnDefinition( columnDefinition ) );

                          if ( mode === 'design' ) {
                              const className : string[] = [];

                              if ( columnDefinition.hidden ) {
                                  className.push( 'yn-disabled' );
                              }
                              if ( !realColumn ) {
                                  className.push( 'yn-disabled' );
                                  className.push( 'yn-error' );
                              }

                              if ( readOnly ) {
                                  className.push( 'yn-readonly' );
                              }

                              retVal[ retVal.length - 1 ].hidden = false;
                              retVal[ retVal.length - 1 ].pt = {
                                  headerCell   : {
                                      id                            : `leid_${columnDefinition.layoutElementId}`,
                                      className                     : className.join( ' ' ),
                                      [ 'data-layoutelementid' ]    : String( columnDefinition.layoutElementId ),
                                      [ 'data-datadescriptoralias' ]: `${columnDefinition.dataDescriptor?.alias ? `${columnDefinition.dataDescriptor.alias} ` : ''}${columnDefinition.dataDescriptor?.fullAlias.split( ',' ).join( ' ' ) ?? ''}`,
                                      draggable                     : !columnDefinition.hidden && realColumn && !readOnly,
                                      style                         : {
                                          [ '--column-height' ]       : `${dataTableHeight}px`,
                                          [ '--column-header-height' ]: `${dataTableHeaderHeight}px`
                                      },
                                      onMouseOver                   : onMouseEnter.bind( null, columnDefinition.layoutElementId ),
                                      onMouseOutCapture             : onMouseLeave.bind( null, columnDefinition.layoutElementId ),
                                      onClickCapture                : onClick.bind( null, columnDefinition.layoutElementId ),
                                      onDragStart                   : !columnDefinition.hidden && realColumn && !readOnly
                                          ? onDragStart.bind( null, columnDefinition.layoutElementId )
                                          : undefined
                                  },
                                  columnResizer: {
                                      onClickCapture    : onClick.bind( null, columnDefinition.layoutElementId ),
                                      onMouseDownCapture: onColumnResizeStart.bind( null, columnDefinition.layoutElementId )
                                  }
                              };
                          }
                      }
                  }
              }

              return retVal;
          },
          buildSearchColumns                      = ( columnDefinitionsToUse = columnDefinitions, columnsToUse = columns ) => {
              const retVal : string[] = [];

              if ( columnDefinitionsToUse && columnsToUse ) {
                  for ( const column of columnsToUse ) {
                      const columnField      = column.field,
                            columnDefinition = columnDefinitionsToUse.find( colDef => colDef.field === columnField ) as ColumnDefinition | undefined;

                      if ( columnDefinition?.searchColumn ) {
                          retVal.push( columnField );
                      }
                  }
              }

              return retVal;
          },
          loadColumnDataDescriptors               = async () => {
              const innerStartTime = debug ? performance.now() : 0;

              try {
                  if ( !isLoading.current && ( query?.type === 'SQL' || query?.type === 'Cypher' ) ) {
                      const params : LoadColumnDataDescriptorsParameters = {
                          type      : query.type,
                          schema    : query.schema,
                          query     : query.query,
                          parameters: query.parameters
                      };

                      if ( elusiveData?.lastColumnDataDescriptorParameters !== JSON.stringify( params ) ) {
                          if ( debug ) {
                              console.groupCollapsed( `%c${'%cOverview - %cloadColumnDataDescriptors%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.loadFunction};`, 'font-weight: normal;' );
                          }

                          isLoading.current = true;

                          dispatch( startRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          await dispatch( loadData(
                              'overview/loadColumnDataDescriptors',
                              configDataContext,
                              configDataContext,
                              'loadColumnDataDescriptors',
                              'CFG',
                              {
                                  params
                              },
                              false,
                              ( result ) => {
                                  dispatch( setElusiveData( uid, {
                                      lastColumnDataDescriptorParameters: JSON.stringify( params ),
                                      columnDataDescriptors             : result.data.columnDataDescriptors,
                                      rawColumns                        : buildRawColumns( dataStructure, result.data.columnDataDescriptors )
                                  } ) );
                              }
                          ) );

                          dispatch( endRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          isLoading.current = false;

                          if ( debug ) {
                              console.log( `Form - %cloadColumnDataDescriptors end - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 106 ), `color: #${debugColorScheme.loadFunction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                              console.groupEnd();
                          }
                      }
                  }
              }
              catch ( e ) {
                  console.error( e );
              }
          },
          startDrag                               = ( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) : void => {
              if ( dataStructure ) {
                  const configDataContextForExtract = 'temp_overviewDesigner_startDrag',
                        rootItem                    = data.items[ rootLayoutElementId ];

                  global.data[ configDataContextForExtract ] = {
                      items     : {
                          ...data.items
                      },
                      references: {
                          ...data.references
                      },
                      schema    : 'CFG'
                  };

                  const rawColumn = dataExtraction.extractColumnData( rootItem, configDataContextForExtract, dataStructure );

                  if ( elusiveData?.columnDataDescriptors?.length ) {
                      const columnDataDescriptor = elusiveData.columnDataDescriptors.find( cDD => cDD.alias === rawColumn.data.queryFieldName );

                      if ( columnDataDescriptor ) {
                          rawColumn.dataDescriptor = columnDataDescriptor;
                          rawColumn.data.valueType = columnDataDescriptor.valueType ?? 'STRING';
                      }
                  }

                  if ( rawColumn ) {
                      rawColumn.data.valueType = rawColumn.dataDescriptor?.valueType ?? 'STRING';

                      newDragInfo.current = {
                          data,
                          rawColumn
                      };

                      onDragStart( rootLayoutElementId, e, options );

                      return;
                  }
              }

              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.preventDefault();
          },
          markLayoutElementsForAlias              = ( alias : string | string[] | undefined ) : void => {
              if ( typeof alias === 'string' ) {
                  alias = [ alias ];
              }

              setAliasToMark( alias );
          },
          setSorters                              = useCallback(
              ( newSorters : ColumnSorter[] ) => {
                  if ( mode === 'design' ) {
                      const rawColumnsToUse                   = lastParams.current?.rawColumns as Column[],
                            columnsDataToSet : {
                                [ itemId : number ] : ItemAttributesSettable
                            }                                 = {},
                            activeStatusId                    = metaModel[ 'CFG' ].getStatus( 'active' )?.id,
                            deletedStatusId                   = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id,
                            sortOrderPropertyTypeIdString     = String( metaModel[ 'CFG' ].getPropertyType( 'SortOrder' )?.id ?? 0 ),
                            sortDirectionPropertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'SortDirection' )?.id ?? 0 );

                      // clear old sort
                      for ( const rawColumn of rawColumnsToUse ) {
                          const columnItem = global.data[ configDataContext ].items[ rawColumn.data.id ];

                          if ( columnItem ) {
                              columnsDataToSet[ rawColumn.data.id ] = {
                                  properties: {}
                              };

                              if ( columnItem.properties.has( Number( sortOrderPropertyTypeIdString ) ) ) {
                                  columnsDataToSet[ rawColumn.data.id ].properties![ sortOrderPropertyTypeIdString ] = {
                                      statusId: deletedStatusId
                                  };
                              }

                              if ( columnItem.properties.has( Number( sortDirectionPropertyTypeIdString ) ) ) {
                                  columnsDataToSet[ rawColumn.data.id ].properties![ sortDirectionPropertyTypeIdString ] = {
                                      statusId: deletedStatusId
                                  };
                              }
                          }
                      }

                      // set new sort
                      if ( newSorters.length ) {
                          for ( let index = 0; index < newSorters.length; index++ ) {
                              const rawColumn = rawColumnsToUse.find( rC => rC.dataDescriptor?.alias === newSorters[ index ].field );

                              if ( rawColumn ) {
                                  columnsDataToSet[ rawColumn.data.id ] = {
                                      properties: {
                                          [ sortOrderPropertyTypeIdString ]    : {
                                              value   : index + 1,
                                              statusId: activeStatusId
                                          },
                                          [ sortDirectionPropertyTypeIdString ]: {
                                              value   : newSorters[ index ].direction,
                                              statusId: activeStatusId
                                          }
                                      }
                                  }
                              }
                          }
                      }

                      // set sort
                      for ( const columnItemId of Object.keys( columnsDataToSet ) ) {
                          dispatch( updateItem(
                              configDataContext,
                              columnItemId,
                              columnsDataToSet[ columnItemId ],
                              additionalElusiveDataContexts
                          ) );
                      }
                  }
                  else {
                      dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, {
                          sorters: newSorters
                      } ) );

                      dataTableSorters = newSorters;
                  }
              },
              [
                  additionalElusiveDataContexts,
                  configDataContext,
                  mode,
                  uid
              ]
          ),
          dataTableElement                        = useMemo(
              () => {
                  return <DataTable
                      ref={dataTable}

                      uid={`${uid}_${dataTableUidExtension}`}

                      schema={dataSchema}

                      dataContext={dataContext}

                      dataStructure={dataStructure}

                      columnDefinitions={columnDefinitions}

                      autoLayout={dataTableComponentProps?.autoLayout ?? overview?.data.columnLayout === 'autoLayout'}
                      forceFit={dataTableComponentProps?.forceFit ?? overview?.data.columnLayout === 'forceFit'}
                      page={dataTableComponentProps?.page}
                      rows={dataTableComponentProps?.rows ?? overview?.data.rowsPerPage}
                      searchString={dataTableComponentProps?.searchString}
                      rowStyling={rowStyling}

                      searchColumns={searchColumns}
                      columns={columns}

                      filters={mode === 'design' ? filters : dataTableComponentProps?.filters}
                      fixFilters={mode === 'design' ? fixFilters : dataTableComponentProps?.fixFilters}
                      onFilterChange={mode === 'design' ? onFilterChange : undefined}
                      onFilterEnterKey={mode === 'design' ? onFilterEnterKey : undefined}

                      sorters={mode === 'design' ? sorters : dataTableComponentProps?.sorters}
                      onSort={mode === 'design' ? onSort : undefined}

                      paginatorTemplate={mode === 'design'
                          ? {
                              'RowsPerPageDropdown': ( options ) => {
                                  return <Dropdown
                                      value={options.value}
                                      options={options.options}
                                      onChange={( event ) => {
                                          onRowsPerPageChange( event );
                                          options.onChange( event as PaginatorChangeEvent );
                                      }}
                                  />;
                              }
                          }
                          : undefined}
                      scrollLeft={dataTableComponentProps?.scrollLeft}
                      scrollTop={dataTableComponentProps?.scrollTop}

                      value={elusiveData?.metaData?.rowData ?? []}
                      valueFieldMapping={elusiveData?.metaData?.rowDataMapping ?? {}}
                      totalRecords={elusiveData?.metaData?.total ?? 0}

                      reload={() => {
                          return true;
                      }}
                      className="yn-datatable-full yn-flex yn-datatable-border"

                      expandedRows={dataTableComponentProps?.expandedRows}
                      {...columnGrouping}
                  />;
              },
              [
                  columnDefinitions,
                  columnGrouping,
                  columns,
                  configRootItemId,
                  dataContext,
                  dataSchema,
                  dataStructure,
                  dataTableComponentProps?.autoLayout,
                  dataTableComponentProps?.expandedRows,
                  dataTableComponentProps?.filters,
                  dataTableComponentProps?.fixFilters,
                  dataTableComponentProps?.forceFit,
                  dataTableComponentProps?.page,
                  dataTableComponentProps?.rows,
                  dataTableComponentProps?.scrollLeft,
                  dataTableComponentProps?.scrollTop,
                  dataTableComponentProps?.searchString,
                  dataTableComponentProps?.sorters,
                  dataTableUidExtension,
                  elusiveData?.metaData?.rowData,
                  elusiveData?.metaData?.rowDataMapping,
                  elusiveData?.metaData?.total,
                  filters,
                  fixFilters,
                  mode,
                  onFilterChange,
                  onFilterEnterKey,
                  onRowsPerPageChange,
                  onSort,
                  overview?.data.columnLayout,
                  overview?.data.rowsPerPage,
                  rootItemFormState,
                  rowStyling,
                  searchColumns,
                  selectedLayoutElementId,
                  sorters,
                  uid
              ]
          );

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            dataStructure,
            columnDefinitions,
            columns,
            filters,
            fixFilters,
            hasHiddenColumns,
            sorters,
            markLayoutElementsForAlias,
            startDrag,
            setSorters
        } )
    );

    // compute dataTable height (on update)
    useLayoutEffect(
        () => {
            const dataTableDivElement       = document.querySelector( '.yn-overview-designer .p-datatable .p-datatable-wrapper' ) as HTMLDivElement,
                  dataTableHeaderDivElement = document.querySelector( '.yn-overview-designer .p-datatable .p-datatable-wrapper .p-datatable-thead' ) as HTMLDivElement;

            if (
                dataTableComponentProps?.autoLayout !== undefined
                && dataTableDivElement?.clientHeight
                && dataTableHeight !== dataTableDivElement.clientHeight
            ) {
                setDataTableHeight( dataTableDivElement.clientHeight );
            }

            if (
                dataTableHeaderDivElement?.clientHeight
                && dataTableHeaderHeight !== dataTableHeaderDivElement.clientHeight
            ) {
                setDataTableHeaderHeight( dataTableHeaderDivElement.clientHeight );
            }
        }
    );

    // compute internal data / load data (on update)
    useEffect(
        () => {
            if ( !isLoading.current ) {
                const innerStartTime                 = debug ? performance.now() : 0,
                      recompute                      = elusiveData === undefined,
                      elusiveDataToSet : ElusiveData = {};

                // dataStructure
                if (
                    recompute
                    || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                    || query !== lastParams.current?.query
                ) {
                    const newDataStructure = buildDataStructure();

                    if ( JSON.stringify( newDataStructure ?? {} ) !== JSON.stringify( dataStructure ?? {} ) ) {
                        dataStructure = newDataStructure;
                        elusiveDataToSet.dataStructure = newDataStructure;
                        dataSchema = getDataSchema( dataStructure );
                    }
                }

                // build rawColumns
                if (
                    recompute
                    || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                    || dataStructure !== lastParams.current?.dataStructure
                    || query !== lastParams.current?.query
                    || mode !== lastParams.current?.mode
                ) {
                    // load columnDataDescriptors for sql or cypher queries
                    if (
                        (
                            query?.type === 'SQL'
                            || query?.type === 'Cypher'
                        )
                        && query.query !== lastParams.current?.query?.query
                    ) {
                        void loadColumnDataDescriptors();
                    }
                    else {
                        const newRawColumns = buildRawColumns( dataStructure, elusiveData?.columnDataDescriptors );

                        if ( JSON.stringify( newRawColumns ?? {} ) !== JSON.stringify( rawColumns ?? {} ) ) {
                            rawColumns = newRawColumns;
                            elusiveDataToSet.rawColumns = newRawColumns;
                        }
                    }
                }

                // stop computing on loading
                if ( !isLoading.current ) {

                    // mode
                    if ( mode !== lastParams.current?.mode ) {
                        dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, {
                            filters,
                            sorters
                        } ) );

                        dataTableFilters = filters;
                        dataTableSorters = sorters;
                    }

                    // build columnDefinitions, fixFilters, filters and sorters
                    if (
                        recompute
                        || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                        || dataStructure !== lastParams.current?.dataStructure
                        || rawColumns !== lastParams.current?.rawColumns
                    ) {
                        const {
                                  columnDefinitions: newColumnDefinitions,
                                  fixFilters       : newFixFilters,
                                  filters          : newFilters,
                                  sorters          : newSorters
                              }                                                = buildColumnDefinitionsFiltersAndSorters( dataStructure, rawColumns ),
                              dataTablePropsToSet : Partial<DataTableAllProps> = {};

                        if ( JSON.stringify( newColumnDefinitions ?? {} ) !== JSON.stringify( columnDefinitions ?? {} ) ) {
                            columnDefinitions = newColumnDefinitions;
                            elusiveDataToSet.columnDefinitions = newColumnDefinitions;
                        }
                        if ( JSON.stringify( newFixFilters ?? {} ) !== JSON.stringify( fixFilters ?? {} ) ) {
                            fixFilters = newFixFilters;
                            elusiveDataToSet.fixFilters = newFixFilters;
                        }
                        if ( JSON.stringify( newFilters ?? {} ) !== JSON.stringify( filters ?? {} ) ) {
                            filters = newFilters;
                            elusiveDataToSet.filters = newFilters;
                            dataTablePropsToSet.filters = undefined;
                        }
                        if ( JSON.stringify( newSorters ?? {} ) !== JSON.stringify( sorters ?? {} ) ) {
                            sorters = newSorters;
                            elusiveDataToSet.sorters = newSorters;
                            dataTablePropsToSet.sorters = undefined;
                        }

                        if ( Object.keys( dataTablePropsToSet ).length ) {
                            dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, dataTablePropsToSet ) );
                        }
                    }

                    // rowStyling
                    if (
                        recompute
                        || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                        || dataStructure !== lastParams.current?.dataStructure
                        || overviewItem !== lastParams.current?.overviewItem
                    ) {
                        const newRowStyling = buildRowStyling( dataStructure );

                        if ( JSON.stringify( newRowStyling ?? {} ) !== JSON.stringify( rowStyling ?? {} ) ) {
                            rowStyling = newRowStyling;
                            elusiveDataToSet.rowStyling = newRowStyling;
                        }
                    }

                    // columns
                    if (
                        recompute
                        || columnDefinitions !== lastParams.current?.columnDefinitions
                        || dataTableHeaderHeight !== lastParams.current?.dataTableHeaderHeight
                        || dataTableHeight !== lastParams.current?.dataTableHeight
                        || mode !== lastParams.current?.mode
                        || showHiddenColumns !== lastParams.current?.showHiddenColumns
                    ) {
                        const newColumns = buildColumns( columnDefinitions );

                        if ( JSON.stringify( newColumns ?? {} ) !== JSON.stringify( columns ?? {} ) ) {
                            columns = newColumns;
                            elusiveDataToSet.columns = newColumns;

                            // clear dataTable columns
                            dataTable.current?.dataTable?.resetResizeColumnsWidth();
                            dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, {
                                columns   : undefined,
                                forceFit  : undefined,
                                autoLayout: undefined
                            } ) );
                        }
                    }
                    // autoLayout / forceFit
                    else if (
                        overview?.data.columnLayout !== lastParams.current?.overview?.data.columnLayout
                    ) {
                        dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, {
                            columns   : undefined,
                            forceFit  : undefined,
                            autoLayout: undefined
                        } ) );
                    }

                    // searchColumns
                    if (
                        recompute
                        || columnDefinitions !== lastParams.current?.columnDefinitions
                        || columns !== lastParams.current?.columns
                    ) {
                        const newSearchColumns = buildSearchColumns( columnDefinitions, columns );

                        if ( JSON.stringify( newSearchColumns ?? {} ) !== JSON.stringify( searchColumns ?? {} ) ) {
                            searchColumns = newSearchColumns;
                            elusiveDataToSet.searchColumns = newSearchColumns;
                        }
                    }

                    // load data
                    if (
                        showData
                        && dataStructure
                        && (
                            !isDataLoaded
                            || columns !== lastParams.current?.columns
                            || fixFilters !== lastParams.current?.fixFilters
                            || dataTableFilters !== lastParams.current?.dataTableFilters
                            || dataTableSorters !== lastParams.current?.dataTableSorters
                            || dataTableComponentProps?.searchString !== lastParams.current?.dataTableSearchString
                            || dataTableComponentProps?.page !== lastParams.current?.dataTablePage
                            || dataTableComponentProps?.rows !== lastParams.current?.dataTableRows
                        )
                    ) {
                        void load( ( dataTableComponentProps ?? {} ) as DataTableAllProps, dataTableFilters ?? [], dataTableSorters ?? [] );
                    }
                    else if ( !showData && isDataLoaded ) {
                        elusiveDataToSet.isDataLoaded = false;
                        elusiveDataToSet.lastLoadParameters = undefined;
                        elusiveDataToSet.metaData = undefined;

                    }
                }

                if ( Object.keys( elusiveDataToSet ).length ) {
                    dispatch( setElusiveData( uid, elusiveDataToSet ) );

                    if ( debug ) {
                        additionalRenderTime.current = innerStartTime - performance.now();
                    }
                }

                lastParams.current = {
                    additionalElusiveDataContexts,
                    columnDefinitions,
                    columns,
                    configDataContext,
                    configRootItemId,
                    dataStructure,
                    dataTableFilters,
                    dataTableHeaderHeight,
                    dataTableHeight,
                    dataTablePage        : dataTableComponentProps?.page,
                    dataTableRows        : dataTableComponentProps?.rows,
                    dataTableSearchString: dataTableComponentProps?.searchString,
                    dataTableSorters,
                    filters,
                    fixFilters,
                    globalConfigDataVersions,
                    hoverLayoutElementId,
                    messageCatalogTimestamp,
                    metaModelTimestamp,
                    mode,
                    onMarkFieldsForAlias,
                    onSelectLayoutElement,
                    overview,
                    overviewItem,
                    query,
                    rawColumns,
                    rowStyling,
                    searchColumns,
                    selectedLayoutElementId,
                    showData,
                    showHiddenColumns,
                    sorters,
                    uid,
                    user
                };
            }
        }
    );

    // mark/unmark dragged layoutElement (on update)
    useLayoutEffect(
        () => {
            // remove current
            const elements : NodeListOf<Element> | null | undefined = document.querySelectorAll( '.yn-overviewdesigner-dragged' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                for ( let i = 0; i < elements.length; i++ ) {
                    elements[ i ].classList.remove( 'yn-overviewdesigner-dragged' );
                }
            }

            // set current
            if ( dragInfo.current?.dragLayoutElementId ) {
                element = document.getElementById( `leid_${dragInfo.current.dragLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-overviewdesigner-dragged' );
                }
            }
        }
    );


    // mark/unmark dropped layoutElement (on update)
    useLayoutEffect(
        () => {
            if ( droppedLayoutElementId.current ) {
                const element = document.getElementById( `leid_${droppedLayoutElementId.current}` );

                if ( element ) {
                    element.classList.add( 'yn-overviewdesigner-dropped' );

                    if ( droppedStyleTimeout.current ) {
                        clearTimeout( droppedStyleTimeout.current );
                    }

                    droppedStyleTimeout.current = setTimeout(
                        () => {
                            droppedLayoutElementId.current = undefined;
                            element.classList.remove( 'yn-overviewdesigner-dropped' );
                        },
                        1600
                    );
                }
            }
        }
    );

    // mark/unmark selected layoutElement (onUpdate)
    useLayoutEffect(
        () => {
            // remove current
            let elements : NodeListOf<Element> | null | undefined = document.querySelectorAll( '.yn-overviewdesigner-selected' );

            if ( elements?.length ) {
                for ( let i = 0; i < elements.length; i++ ) {
                    elements[ i ].classList.remove( 'yn-overviewdesigner-selected' );
                }
            }

            // set current
            if ( selectedLayoutElementId ) {
                elements = document.querySelectorAll( `[data-layoutelementid="${selectedLayoutElementId}"]` );

                if ( elements?.length ) {
                    for ( let i = 0; i < elements.length; i++ ) {
                        elements[ i ].classList.add( 'yn-overviewdesigner-selected' );
                    }
                }
            }
        }
    );

    // mark/unmark aliasToMark
    useLayoutEffect(
        () => {
            // remove current
            let elements : NodeListOf<Element> | null | undefined = document.querySelectorAll( '.yn-overviewdesigner-using-alias' ),
                index                                             = 0;

            if ( elements?.length ) {
                for ( let i = 0; i < elements.length; i++ ) {
                    elements[ i ].classList.remove( 'yn-overviewdesigner-using-alias' );
                }
            }

            if ( aliasToMark ) {
                const selector : string[] = [];

                for ( const alias of aliasToMark ) {
                    //selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias~="${alias}"]` );
                    //selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias^="${alias}"]` );
                    //selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias$="${alias}"]` );
                    selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias*="${alias}"]` );
                }

                elements = document.querySelectorAll( selector.join( ', ' ) );
                index = 0;

                if ( elements?.length ) {
                    while ( elements[ index ] ) {
                        elements[ index++ ].classList.add( 'yn-overviewdesigner-using-alias' );
                    }
                }
            }
        },
        [
            aliasToMark
        ]
    );

    // mark/unmark hovered layoutElement
    useLayoutEffect(
        () => {
            // remove current
            const elements : NodeListOf<Element> | null | undefined = document.querySelectorAll( '.yn-overviewdesigner-hover' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                for ( let i = 0; i < elements.length; i++ ) {
                    elements[ i ].classList.remove( 'yn-overviewdesigner-hover' );
                }
            }

            // set current
            if ( hoverLayoutElementId ) {
                element = document.getElementById( `leid_${hoverLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-overviewdesigner-hover' );
                }
            }
        },
        [
            hoverLayoutElementId,
            columns
        ]
    );

    if ( debug ) {
        console.log( `%crender - ${Math.round( 1000 * ( performance.now() - ( startTime + additionalRenderTime.current ) ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.render}; font-weight: bold;`, 'font-weight: unset;' );
        additionalRenderTime.current = 0;
    }

    if ( mode === 'input' ) {
        if ( standalone ) {
            return dataTableElement;
        }
        else {
            return <div
                id={`${uid}_overview`}
                className="yn-card-content-panel yn-flex-layout-parent"
                style={{ maxWidth: '120rem', alignSelf: 'center' }}
            >
                <Card className="yn-flex-layout-parent" style={{ width: '100%', maxWidth: '100%' }}>
                    {dataTableElement}
                </Card>
            </div>;
        }
    }
    else {
        const classNames = [
            'yn-card-content-panel',
            'yn-flex-layout-parent',
            'yn-overview-designer'
        ];

        if ( rootItemFormState === 'ERROR' ) {
            classNames.push( 'yn-overview-designer-error' );
        }
        else if ( rootItemFormState === 'DIRTY' ) {
            classNames.push( 'yn-overview-designer-dirty' );
        }
        else if ( selectedLayoutElementId === configRootItemId ) {
            classNames.push( 'yn-overview-designer-selected' );
        }

        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        return <div
            id={`${uid}_overview`}
            className={classNames.join( ' ' )}
            onClick={onOverviewClick}
            onMouseLeave={onOverviewLeave}
        >
            <Card className="yn-flex-layout-parent" style={{ width: '100%', maxWidth: '100%' }}>
                {dataTableElement}
            </Card>
        </div>;
    }
} );

OverviewDesigner.defaultProps = defaultProps;
OverviewDesigner.displayName = 'OverviewDesigner';

export default OverviewDesigner;
